import { useEffect, useState, useRef, type ReactNode } from "react";
import { initializeB24Frame } from "@bitrix24/b24jssdk";
import { useAuthStore } from "@/stores/auth.store";
import type { CurrentUser } from "@/stores/auth.store";
import { BatchOrchestrator } from "@/lib/BatchOrchestrator";
import { ProxyOrchestrator } from "@/lib/ProxyOrchestrator";
import { loadStandaloneSession, consumeCaptureFromUrl } from "@/lib/dev-standalone-session";
import { DevStandaloneControls } from "@/components/DevStandaloneControls";
import { BootScreen } from "@/components/ui/BootScreen";
import { InstallFlow } from "@/components/ui/InstallFlow";

// ═══════════════════════════════════════════════════════════
// BitrixProvider — SDK init via @bitrix24/b24jssdk (TPP §5.1)
// Reads DOMAIN, APP_SID from window.name injected by the B24
// iframe POST handler. The app MUST be opened inside Bitrix24.
// ═══════════════════════════════════════════════════════════

// ─── Token refresh constants ──────────────────────────────
// Check whether the token is about to expire every 2 minutes.
const REFRESH_CHECK_INTERVAL_MS = 2 * 60 * 1_000;
// Refresh proactively when fewer than 10 minutes remain before expiry.
// expires_in from B24 is a Unix timestamp in milliseconds (§3.1 MASTER_PROTOCOL).
const REFRESH_THRESHOLD_MS = 10 * 60 * 1_000;

interface BitrixProviderProps {
  children: ReactNode;
}

export function BitrixProvider({ children }: BitrixProviderProps) {
  const [ready, setReady] = useState(false);
  const [repairNeeded, setRepairNeeded] = useState(false);

  const setAuth = useAuthStore((s) => s.setAuth);
  const setB24Frame = useAuthStore((s) => s.setB24Frame);
  const setOrchestrator = useAuthStore((s) => s.setOrchestrator);
  const setCurrentUser = useAuthStore((s) => s.setCurrentUser);
  const setStandalone = useAuthStore((s) => s.setStandalone);

  // Stable ref so the cleanup closure always cancels the correct timer
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const b24Ref = useRef<Awaited<ReturnType<typeof initializeB24Frame>> | null>(null);

  useEffect(() => {
    let destroyed = false;

    function startTokenRefreshLoop(frame: Awaited<ReturnType<typeof initializeB24Frame>>) {
      refreshTimerRef.current = setInterval(async () => {
        if (destroyed) return;

        try {
          const auth = frame.auth.getAuthData();

          if (auth === false) {
            // Token already expired — refresh immediately
            await frame.auth.refreshAuth();
            return;
          }

          // expires_in is a Unix timestamp in milliseconds (not a duration)
          const msUntilExpiry = auth.expires_in - Date.now();
          if (msUntilExpiry <= REFRESH_THRESHOLD_MS) {
            await frame.auth.refreshAuth();
          }
        } catch (err) {
          console.warn("[BitrixProvider] Token refresh failed:", err);
        }
      }, REFRESH_CHECK_INTERVAL_MS);
    }

    async function init() {
      // ── Standalone Dev Mode (DEBUGGING.md §5) ──────────────────────────
      // Dev-build only, and only when a session was explicitly captured
      // (DevStandaloneControls) while running for real inside Bitrix24.
      // Skips the iframe handshake entirely — REST calls route through
      // ProxyOrchestrator → the server's D1-backed OAuth client instead.
      if (import.meta.env.DEV) {
        // A capture handoff (fresh top-level navigation from the "Capture
        // Dev Session" button) takes priority — it also persists to
        // localStorage for every subsequent load, so this only matters once.
        const standalone = consumeCaptureFromUrl() ?? loadStandaloneSession();
        if (standalone) {
          setAuth({
            memberId: standalone.memberId,
            domain: standalone.domain,
            userId: standalone.userId,
            isAdmin: standalone.isAdmin,
          });
          setCurrentUser(standalone.currentUser);
          setOrchestrator(new ProxyOrchestrator());
          setStandalone(true);
          setReady(true);
          return;
        }
      }

      try {
        const b24 = await initializeB24Frame();
        b24Ref.current = b24;

        if (destroyed) {
          b24.destroy();
          return;
        }

        const authData = b24.auth.getAuthData();
        if (!authData) throw new Error("No auth data from B24 frame");

        // SDK-authoritative admin flag — comes from the B24 iframe context
        const sdkIsAdmin = b24.auth.isAdmin;
        console.log("[BitrixProvider] sdk isAdmin:", sdkIsAdmin, "| userId:", authData.user_id);

        // Boot batch: fetch user profile and app info (for install safety check)
        const batchResult = await b24.actions.v2.batch.make({
          calls: {
            PROFILE: { method: "user.current", params: {} },
            APP_INFO: { method: "app.info", params: {} },
          },
          options: { isHaltOnError: false },
        });

        const batchData = batchResult.getData() as Record<string, unknown>;

        // Safety check: is app marked as uninstalled by B24?
        const appInfo = (batchData["APP_INFO"] ?? {}) as Record<string, unknown>;
        if (appInfo["INSTALLED"] === false || appInfo["INSTALLED"] === "N") {
          console.warn("[BitrixProvider] App is marked as uninstalled. Triggering repair flow.");
          setRepairNeeded(true);
        }

        const profile = (batchData["PROFILE"] ?? {}) as Record<string, unknown>;

        const firstName = String(profile["NAME"] ?? "");
        const lastName = String(profile["LAST_NAME"] ?? "");
        const fullName = [firstName, lastName].filter(Boolean).join(" ") || "User";
        const workPosition = String(profile["WORK_POSITION"] ?? "");
        const photo =
          typeof profile["PERSONAL_PHOTO"] === "string" ? profile["PERSONAL_PHOTO"] : null;

        const serverAdmin = profile["ADMIN"] === true || profile["ADMIN"] === "Y";
        const isAdminFromServer = serverAdmin || sdkIsAdmin;

        setAuth({
          memberId: authData.member_id,
          domain: b24.getTargetOrigin().replace(/^https?:\/\//, ""),
          userId: String(authData.user_id ?? "0"),
          isAdmin: isAdminFromServer,
        });

        const currentUser: CurrentUser = {
          id: String(profile["ID"] ?? authData.user_id ?? "0"),
          fullName,
          workPosition,
          photo,
          isAdmin: isAdminFromServer,
        };
        setCurrentUser(currentUser);

        setB24Frame(b24);

        const orchestrator = new BatchOrchestrator(b24);
        orchestrator.pruneCache();
        setOrchestrator(orchestrator);

        await b24.parent.fitWindow();
        const observer = new ResizeObserver(() => {
          b24.parent.fitWindow().catch(() => {});
        });
        observer.observe(document.body);

        startTokenRefreshLoop(b24);

        setReady(true);
      } catch (err: unknown) {
        console.error("[BitrixProvider] SDK init failed:", err);
        if (!destroyed) {
          // If we failed entirely, we still want to clear the boot screen
          // and let the !memberId guard handle the error presentation.
          setReady(true);
        }
      }
    }

    init();

    return () => {
      destroyed = true;
      if (refreshTimerRef.current !== null) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (b24Ref.current) {
        b24Ref.current.destroy();
        b24Ref.current = null;
      }
    };
  }, [setAuth, setB24Frame, setCurrentUser, setOrchestrator, setStandalone]);

  const memberId = useAuthStore((s) => s.memberId);

  if (!ready) {
    return <BootScreen />;
  }

  if (!memberId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-sm">
          <p className="text-lg font-semibold">Open inside Bitrix24</p>
          <p className="text-sm text-muted-foreground mt-2">
            This application must be launched from within your Bitrix24 portal.
            {import.meta.env.DEV &&
              " In dev, you can also capture a session once inside a real portal and reload standalone — see DEBUGGING.md §5."}
          </p>
        </div>
      </div>
    );
  }

  if (repairNeeded) {
    return (
      <InstallFlow
        mode="repair"
        b24Instance={b24Ref.current}
        onComplete={() => {
          window.location.reload();
        }}
      />
    );
  }

  return (
    <>
      {children}
      {import.meta.env.DEV && <DevStandaloneControls />}
    </>
  );
}
